// cesium-camera.ts

import {
  BoundingSphere,
  Cartesian2,
  Cartesian3,
  Cartographic,
  Math as CesiumMath,
  Color,
  HeadingPitchRange,
  Rectangle,
} from "cesium";
import type {
  CameraOptions,
  CesiumViewSettings,
  InitializeCesiumOptions,
} from "./cesium-types";

// Note: getViewer is imported dynamically to avoid circular imports

// Helper function to validate a single position
function isValidPosition(position: any): boolean {
  return (
    position &&
    typeof position.x === "number" &&
    typeof position.y === "number" &&
    typeof position.z === "number" &&
    !isNaN(position.x) &&
    !isNaN(position.y) &&
    !isNaN(position.z) &&
    isFinite(position.x) &&
    isFinite(position.y) &&
    isFinite(position.z)
  );
}

// State management for camera operations
let initialFlightStarted = false;
let isViewerReady = false;
let initialCameraSetRef = false;
const cameraReady = false;
let lastCameraPosition: CesiumViewSettings | null = null;
const DEBOUNCE_DELAY = 150; // 150ms delay for camera move events
let debounceTimer: any = null;

// Helper function to ensure globe visibility
export function ensureGlobeVisibility(viewer: any) {
  if (!viewer || initialFlightStarted) return;
  viewer.scene.globe.baseColor = Color.TRANSPARENT;
  viewer.scene.backgroundColor = Color.TRANSPARENT;
  initialFlightStarted = true;
}

// Helper function to create camera destination and orientation
export function createCameraOptions(
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
): CameraOptions {
  const height = Math.max(100, settings.height || 1000);

  return {
    destination: Cartesian3.fromDegrees(
      settings.center[0],
      settings.center[1],
      height,
    ),
    orientation: {
      heading: settings.bearing ? CesiumMath.toRadians(settings.bearing) : 0,
      pitch: settings.pitch
        ? CesiumMath.toRadians(settings.pitch)
        : -CesiumMath.PI_OVER_TWO,
      roll: 0.0,
    },
    duration: duration,
    complete: onComplete,
  };
}

// Convert current camera position to CesiumViewSettings
export function getCurrentCameraPosition(
  viewer: any,
): CesiumViewSettings | null {
  if (!viewer) return null;

  const cartographic = Cartographic.fromCartesian(viewer.camera.position);
  return {
    center: [
      CesiumMath.toDegrees(cartographic.longitude),
      CesiumMath.toDegrees(cartographic.latitude),
    ],
    height: cartographic.height,
    bearing: CesiumMath.toDegrees(viewer.camera.heading),
    pitch: CesiumMath.toDegrees(viewer.camera.pitch),
  };
}

// Main function for shouldFlyTo pattern (matches CesiumMap exactly)
export function flyToPosition(
  flyToSettings: CesiumViewSettings,
  cesiumCameraPosition?: CesiumViewSettings,
  viewer?: any,
) {
  // Match CesiumMap's exact condition check
  if (
    !isViewerReady ||
    !initialCameraSetRef ||
    !viewer ||
    !cesiumCameraPosition ||
    !flyToSettings
  ) {
    return;
  }

  // Add 10ms delay before flying (exactly like CesiumMap)
  setTimeout(() => {
    if (!viewer) return;

    const cameraOptions = createCameraOptions(flyToSettings, 2.0);
    viewer.camera.flyTo(cameraOptions);
  }, 10);
}

// Unified camera transition function
export function flyToView(
  viewer: any,
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
) {
  if (!viewer) return;

  ensureGlobeVisibility(viewer);

  // Use setTimeout like CesiumMap for consistent behavior
  setTimeout(() => {
    if (!viewer) return;

    const cameraOptions = createCameraOptions(settings, duration, onComplete);
    viewer.camera.flyTo(cameraOptions);
  }, 10);
}

// Alias for backward compatibility
export function updateCameraPosition(
  viewer: any,
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
) {
  flyToView(viewer, settings, duration, onComplete);
}

// Set camera position immediately (without animation)
export function setCameraPosition(viewer: any, settings: CesiumViewSettings) {
  if (!viewer) return;

  ensureGlobeVisibility(viewer);

  const cameraOptions = createCameraOptions(settings);
  viewer.camera.setView({
    destination: cameraOptions.destination,
    orientation: cameraOptions.orientation,
  });
}

// Function to check and update tileset visibility based on camera height
function updateTilesetVisibility(
  viewer: any,
  tilesetRegistry: Map<string | number, any>,
) {
  if (!viewer || !tilesetRegistry) return;

  const cameraHeight = viewer.camera.positionCartographic.height;
  const isVisible = cameraHeight < 1000; // Visible if camera is below 1000 meters

  tilesetRegistry.forEach((tileset) => {
    if (tileset.show !== isVisible) {
      tileset.show = isVisible;
    }
  });
}

// Setup camera event listeners
export function setupCameraEvents(
  viewer: any,
  tilesetRegistry: Map<string | number, any>,
  onCameraMove?: (position: CesiumViewSettings) => void,
  onViewCenterChange?: InitializeCesiumOptions["onViewCenterChange"],
) {
  if (!viewer) return;

  const handleCameraMove = () => {
    // Update tileset visibility on every camera move
    updateTilesetVisibility(viewer, tilesetRegistry);

    // Debounce the onCameraMove callback for performance
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const currentPosition = getCurrentCameraPosition(viewer);
      if (
        currentPosition &&
        JSON.stringify(currentPosition) !== JSON.stringify(lastCameraPosition)
      ) {
        lastCameraPosition = currentPosition;
        onCameraMove?.(currentPosition);
      }
    }, DEBOUNCE_DELAY);
  };

  const handleCenterChange = () => {
    if (!onViewCenterChange) return;

    const centerScreenPosition = new Cartesian2(
      viewer.canvas.clientWidth / 2,
      viewer.canvas.clientHeight / 2,
    );

    // Try to pick an object first to see if there's a 3D tileset/model
    const pickedObject = viewer.scene.pick(centerScreenPosition);
    let centerPosition: any;

    if (pickedObject && pickedObject.primitive) {
      // If we picked a 3D object, use pickPosition to get the 3D position
      centerPosition = viewer.scene.pickPosition(centerScreenPosition);
    }

    // If no 3D object was picked or pickPosition failed, fallback to terrain
    if (!centerPosition || !isValidPosition(centerPosition)) {
      const ray = viewer.camera.getPickRay(centerScreenPosition);
      centerPosition = viewer.scene.globe.pick(ray, viewer.scene);
    }

    if (centerPosition && isValidPosition(centerPosition)) {
      const centerCartographic = Cartographic.fromCartesian(centerPosition);
      const centerLongitude = CesiumMath.toDegrees(
        centerCartographic.longitude,
      );
      const centerLatitude = CesiumMath.toDegrees(centerCartographic.latitude);

      onViewCenterChange({
        latitude: centerLatitude,
        longitude: centerLongitude,
        height: centerCartographic.height,
      });
    }
  };

  viewer.camera.moveEnd.addEventListener(handleCameraMove);
  viewer.camera.moveEnd.addEventListener(handleCenterChange);
}

// Function to fly the camera to a specific feature (entity)
export function flyToFeature(viewer: any, entity: any, duration = 1.5) {
  if (!viewer || !entity) {
    console.log("❌ flyToFeature: Missing viewer or entity", {
      viewer: !!viewer,
      entity: !!entity,
    });
    return;
  }

  console.log("🛫 flyToFeature called with entity:", {
    hasPolygon: !!entity.polygon,
    hasPolyline: !!entity.polyline,
    hasPosition: !!entity.position,
    name: entity.name,
  });

  const heading = viewer.camera.heading;
  const pitch = -0.6;

  try {
    if (entity.polygon) {
      console.log("✈️ Flying to polygon entity");
      // For polygons, use default flyTo which fits the whole polygon nicely
      viewer.flyTo(entity, {
        duration,
        offset: new HeadingPitchRange(heading, pitch, 0),
      });
    } else if (entity.polyline) {
      console.log("✈️ Flying to polyline entity");

      // OPTION 1: Fixed offset (current approach)
      // viewer.flyTo(entity, {
      //   duration,
      //   offset: new HeadingPitchRange(heading, pitch, 400),
      // });

      // OPTION 2: Maximum height limit
      // viewer.flyTo(entity, {
      //   duration,
      //   maximumHeight: 1500, // Prevents flying too high
      // });

      // OPTION 3: Multiplier to scale the automatic bounding sphere
      // viewer.flyTo(entity, {
      //   duration,
      //   multiplier: 0.5, // 50% of default distance (closer)
      // });

      // OPTION 4: Custom bounding sphere calculation
      const boundingSphere = BoundingSphere.fromPoints(
        entity.polyline.positions._value,
      );
      const scaledRadius = boundingSphere.radius * 2.0; // Scale the radius
      viewer.camera.flyToBoundingSphere(boundingSphere, {
        duration,
        offset: new HeadingPitchRange(heading, -1.0, scaledRadius),
      });

      // OPTION 5: Combination approach
      // viewer
      //   .flyTo(entity, {
      //     duration,
      //     maximumHeight: 2000,
      //     // offset: new HeadingPitchRange(heading, pitch, 400),
      //   })
      //   .then(() => {
      //     console.log("✅ Successfully flew to polyline");
      //   })
      //   .catch((error: any) => {
      //     console.log("❌ Error flying to polyline:", error);
      //   });
    } else if (entity.position) {
      console.log("✈️ Flying to point entity");
      // For point features, fly to the entity with a specific offset
      viewer.flyTo(entity, {
        duration,
        offset: new HeadingPitchRange(heading, pitch, 800), // Use offset for better viewing distance
      });
    } else {
      console.log("❌ Entity has no recognizable geometry for flying");
    }
  } catch (error) {
    console.log("❌ Exception in flyToFeature:", error);
  }
}

// Camera state management functions
export function setCameraReady(ready: boolean) {
  isViewerReady = ready;
  initialCameraSetRef = ready;
}

export function resetCameraState() {
  initialFlightStarted = false;
  isViewerReady = false;
  initialCameraSetRef = false;
}

export function getCameraState() {
  return {
    initialFlightStarted,
    isViewerReady,
    initialCameraSetRef,
  };
}
